import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAtomValue } from 'jotai';
import { useNavigate } from '@tanstack/react-router';
import { BlockButton } from '@/components/BlockButton';
import Input from '@/components/Input';
import SelectBox from '@/components/SelectBox';
import { Textarea } from '@/components/Textarea';
import usePostJobChangeProcess from '../../api/usePostJobChangeProcess';
import { showSuccessToast, showErrorToast } from '../../lib/toast';
import { currentSelectjobChangeProcessAtom } from '../../atoms/jobChangeProcess';
import useFetchActiveUsers from '@/features/agent/api/useFetchActiveUsers';
import useFetchIam from '@/features/agent/api/useFetchIam';

// 売上フォームのZodスキーマ（「売上を登録して変更」ボタン押下時に全項目を必須チェック）
const profitSchema = z.object({
  profit_value: z
    .number({ error: '売上金額は必須です' })
    .int('売上金額は整数で入力してください')
    .nonnegative('売上金額は0以上で入力してください'),
  entry_expect_date: z.string().min(1, '入社予定日は必須です'),
  memo: z.string().max(1000, 'メモは1000文字以内で入力してください').optional().or(z.literal('')),
  // SelectBoxはstring値を返すため、coerceでnumberに変換
  organization_user_id: z.coerce
    .number({ error: '担当者は必須です' })
    .int()
    .positive('担当者を選択してください'),
});

type ProfitFormValues = z.infer<typeof profitSchema>;

type Props = {
  jobChangeProcessId: number;
  close: () => void;
  jobchangeProcessesRefetch: () => Promise<void>;
};

// 年収文字列を数値（円）にパースする（例: "500万円" → 5000000, "5,000,000" → 5000000）
const parseSalary = (salary: string | null | undefined): number | null => {
  if (!salary) return null;
  // 「万円」表記を処理
  const manEnMatch = salary.match(/^[\s]*([0-9,]+(?:\.[0-9]+)?)\s*万/);
  if (manEnMatch) {
    const num = parseFloat(manEnMatch[1].replace(/,/g, ''));
    return isNaN(num) ? null : Math.round(num * 10000);
  }
  // 数値のみ（カンマ・円記号を除去）
  const cleaned = salary.replace(/[,、円¥\s]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : Math.round(num);
};

// 今から2ヶ月後の1日をYYYY-MM-DD形式で返す
const getDefaultEntryExpectDate = (): string => {
  const now = new Date();
  const date = new Date(now.getFullYear(), now.getMonth() + 2, 1);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

export default function StatusCloseModalContents({
  jobChangeProcessId,
  close,
  jobchangeProcessesRefetch,
}: Props) {
  const [isSubmit, setIsSubmit] = useState(false);
  const { mutateAsync } = usePostJobChangeProcess();
  const navigate = useNavigate();

  // 現在選択中の選考プロセス（候補者の担当者情報・売上計算に使用）
  const currentJobChangeProcess = useAtomValue(currentSelectjobChangeProcessAtom);

  // 組織ユーザー一覧（既存の active_users API を使用）
  const { data: activeUsersData } = useFetchActiveUsers();
  const organizationUsers = activeUsersData?.records ?? [];

  // ログインユーザー情報（担当者のデフォルト値取得に使用）
  const { data: iamData } = useFetchIam();

  // 担当者のデフォルト値: 候補者の最初の担当者 → ログインユーザー → undefined
  const defaultOrganizationUserId =
    currentJobChangeProcess?.candidate_profile?.organization_users?.[0]?.id ??
    iamData?.current_user?.id;

  // 売上金額の自動計算: 企業契約フィー × 年収 > 求人フィーRate × 年収 > 求人固定フィー
  const defaultProfitValue = useMemo(() => {
    const jcp = currentJobChangeProcess;
    if (!jcp) return undefined;

    const salary = parseSalary(jcp.candidate_profile?.current_salary);
    const companyFeeRate = jcp.company_fee_rate;
    const sharedJobPost = jcp.provided_job_post?.shared_job_post;
    const jobFeeRate = sharedJobPost?.strict_fee_rate;
    const jobFeeValue = sharedJobPost?.strict_fee_value;

    // 企業契約のフィーRate × 候補者年収（0 を有効値として扱うため null/undefined 比較）
    if (companyFeeRate != null && salary != null) {
      return Math.round((companyFeeRate / 100) * salary);
    }
    // 求人のフィーRate × 候補者年収
    if (jobFeeRate != null && salary != null) {
      return Math.round((jobFeeRate / 100) * salary);
    }
    // 求人の固定フィー金額
    if (jobFeeValue != null) {
      return jobFeeValue;
    }

    return undefined;
  }, [currentJobChangeProcess]);

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm<z.input<typeof profitSchema>, unknown, ProfitFormValues>({
    resolver: zodResolver(profitSchema),
    defaultValues: {
      profit_value: defaultProfitValue,
      entry_expect_date: getDefaultEntryExpectDate(),
      memo: '',
      organization_user_id: defaultOrganizationUserId,
    },
  });

  // 「売上登録せずに確定」: 売上データなしでステータスのみ変更
  const onSubmitWithoutProfit = async () => {
    setIsSubmit(true);
    try {
      await mutateAsync({ jobChangeProcessId, status: 'close' });
      showSuccessToast('ステータスを変更しました');
    } catch {
      showErrorToast('ステータスの変更に失敗しました');
    } finally {
      await jobchangeProcessesRefetch();
      setIsSubmit(false);
      close();
    }
  };

  // 「売上を登録して確定」: バリデーション通過後に売上データ付きでリクエスト
  const onSubmitWithProfit = async (data: ProfitFormValues) => {
    setIsSubmit(true);
    try {
      await mutateAsync({
        jobChangeProcessId,
        status: 'close',
        profit: {
          profit_value: data.profit_value,
          entry_expect_date: data.entry_expect_date,
          // 入金予定日は入社予定日と同一
          payment_date: data.entry_expect_date,
          memo: data.memo || undefined,
          organization_user_id: data.organization_user_id,
        },
      });
      showSuccessToast('売上を登録してステータスを変更しました');
      await jobchangeProcessesRefetch();
      close();
      // 売上管理画面へリダイレクト
      navigate({ to: '/agent/profits' });
    } catch {
      showErrorToast('ステータスの変更に失敗しました');
    } finally {
      setIsSubmit(false);
    }
  };

  // 「売上を登録して確定」ボタン押下時: バリデーションを実行してからsubmit
  const handleSubmitWithProfit = async () => {
    const isValid = await trigger();
    if (!isValid) return;
    await handleSubmit(onSubmitWithProfit)();
  };

  return (
    <div className="w-[640px] bg-white">
      {/* ヘッダー */}
      <div className="px-6 py-4">
        <p className="text-base font-bold">変更の確認</p>
      </div>

      {/* 本文・フォーム */}
      <div className="border-y border-solid border-border px-6 py-4">
        <p className="mb-4 text-base">ステータスを「内定承諾」に変更しますか？</p>

        {/* 売上フォーム */}
        <div className="space-y-4">
          {/* 売上金額 */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[#262626]">
              売上金額 <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                {...register('profit_value', { valueAsNumber: true })}
                placeholder="例: 500000"
                className="w-full"
              />
              <span className="shrink-0 text-sm text-[#262626]">円</span>
            </div>
            {defaultProfitValue != null && (
              <p className="mt-1 text-xs text-gray-500">
                フィーレートと候補者年収から自動計算されました
              </p>
            )}
            {errors.profit_value && (
              <p className="mt-1 text-sm text-red-600">{errors.profit_value.message}</p>
            )}
          </div>

          {/* 入社予定日 */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[#262626]">
              入社予定日 <span className="text-red-500">*</span>
            </label>
            <Input type="date" {...register('entry_expect_date')} className="w-full" />
            {errors.entry_expect_date && (
              <p className="mt-1 text-sm text-red-600">{errors.entry_expect_date.message}</p>
            )}
          </div>

          {/* 担当者 */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[#262626]">
              担当者 <span className="text-red-500">*</span>
            </label>
            <SelectBox
              {...register('organization_user_id')}
              defaultValue={defaultOrganizationUserId ? String(defaultOrganizationUserId) : ''}
              placeholder="担当者を選択してください"
              options={organizationUsers.map((user) => ({
                value: String(user.id),
                label: user.name,
              }))}
            />
            {errors.organization_user_id && (
              <p className="mt-1 text-sm text-red-600">{errors.organization_user_id.message}</p>
            )}
          </div>

          {/* メモ */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[#262626]">メモ</label>
            <Textarea {...register('memo')} rows={3} error={errors.memo?.message} />
          </div>
        </div>
      </div>

      {/* ボタンエリア */}
      <div className="flex justify-end gap-4 px-6 py-4">
        {/* キャンセル */}
        <div className="w-[94px]">
          <BlockButton
            disabled={isSubmit}
            colorType="white"
            type="button"
            text="キャンセル"
            onClick={close}
          />
        </div>

        {/* 売上登録せずに確定（バリデーションなしでステータスのみ変更） */}
        <div className="w-[160px]">
          <BlockButton
            disabled={isSubmit}
            colorType="white"
            type="button"
            text="売上登録せずに確定"
            onClick={onSubmitWithoutProfit}
          />
        </div>

        {/* 売上を登録して確定（バリデーションあり） */}
        <div className="w-[180px]">
          <BlockButton
            disabled={isSubmit}
            type="button"
            text="売上を登録して確定"
            onClick={handleSubmitWithProfit}
          />
        </div>
      </div>
    </div>
  );
}
